import React, { useCallback, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';

import {
  Account,
  BuildConfiguration,
  Capabilities,
  EditorEnum,
  Repository,
  Validator,
} from 'src/components/pipelines/models';

import {
  DOCS_DEPLOYMENTS,
  VALID_ENVIRONMENT_NAME,
  YML_COMMIT_MESSAGE,
  YML_DEPLOYMENTS_COMMIT_MESSAGE,
  YML_FILE_NAME,
} from '../const';
import {
  getBranchPermissionsPageURL,
  getForkPageURL,
  getSettingsPageURL,
} from '../utils/urlUtil';

import {
  CommitButton,
  CommitSectionWrapper,
  LearnMore,
  ValidationEmoji,
  ValidationMessage,
} from './styled';

type Props = {
  account: Account;
  buildConfiguration: BuildConfiguration;
  repository: Repository;
  capabilities: Capabilities;
  validator: Validator;
  commitConfiguration: (configuration: string, commitMessage: string) => void;
  enablePipelines: () => void;
};

const CommitSection = ({
  account,
  buildConfiguration,
  capabilities,
  repository,
  validator,
  enablePipelines,
  commitConfiguration,
}: Props) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [isEnabling, setIsEnabling] = useState(false);

  const sendUIAnalyticsEvent = useCallback(
    (subjectId: string) => {
      createAnalyticsEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: subjectId,
        source:
          validator.currentEditor === EditorEnum.PipelinesOnboarding
            ? 'pipelinesGettingStartedScreen'
            : 'deploymentsGettingStartedScreen',
      }).fire();
    },
    [createAnalyticsEvent, validator.currentEditor]
  );

  const onCommitOrEnable = useCallback(() => {
    setIsEnabling(true);
    const commitMessage =
      validator.currentEditor === EditorEnum.PipelinesOnboarding
        ? YML_COMMIT_MESSAGE
        : YML_DEPLOYMENTS_COMMIT_MESSAGE;

    if (capabilities.pipelinesEnabled) {
      commitConfiguration(
        buildConfiguration.yml !== validator.code ? validator.code : '',
        commitMessage
      );
    } else {
      enablePipelines();
      commitConfiguration(
        buildConfiguration.yml !== validator.code ? validator.code : '',
        commitMessage
      );
    }
  }, [
    setIsEnabling,
    commitConfiguration,
    enablePipelines,
    buildConfiguration,
    capabilities,
    validator,
  ]);

  const ymlHasDeployments = useMemo(() => {
    return new RegExp(
      `deployment: (${VALID_ENVIRONMENT_NAME})( #.*)?$`,
      'gm'
    ).test(validator.code);
  }, [validator.code]);

  const isButtonDisabled = useMemo(() => {
    if (validator.currentEditor === EditorEnum.DeploymentsOnboarding) {
      return (
        isEnabling ||
        !validator.isValid ||
        (!repository.userIsAdmin && !capabilities.pipelinesEnabled) ||
        !repository.canWriteMainBranch ||
        !ymlHasDeployments
      );
    }

    if (validator.currentEditor === EditorEnum.PipelinesOnboarding) {
      return (
        isEnabling ||
        !validator.isValid ||
        !repository.userIsAdmin ||
        !repository.canWriteMainBranch
      );
    }
    return undefined;
  }, [capabilities, validator, repository, isEnabling, ymlHasDeployments]);

  const pipelinesValidationMessage = useMemo(() => {
    if (!repository.userIsAdmin) {
      return (
        <>
          <ValidationEmoji>🔒</ValidationEmoji>
          <ValidationMessage>
            <h5>You need to be an admin to configure Pipelines</h5>
            <p>
              <a
                href={getForkPageURL(repository.path)}
                target="_top"
                onClick={() => sendUIAnalyticsEvent('forkRepositoryLink')}
              >
                Fork
              </a>{' '}
              this repository or contact the admin to give you rights to
              configure Pipelines.
            </p>
          </ValidationMessage>
        </>
      );
    }
    if (
      repository.hasFetchedCanWriteMainBranch &&
      !repository.canWriteMainBranch
    ) {
      return (
        <>
          <ValidationEmoji>🔒</ValidationEmoji>
          <ValidationMessage>
            <h5>
              Branch permissions on main branch are preventing from adding
              configuration
            </h5>
            <p>
              You can review your{' '}
              <a
                href={getBranchPermissionsPageURL(repository.path)}
                target="_top"
                onClick={() => sendUIAnalyticsEvent('branchPermissionsLink')}
              >
                branch permissions{' '}
              </a>
              or enable Pipelines in{' '}
              <a
                href={getSettingsPageURL(repository.path, account.addonKey)}
                target="_top"
                onClick={() => sendUIAnalyticsEvent('pipelinesSettingsLink')}
              >
                settings
              </a>
            </p>
          </ValidationMessage>
        </>
      );
    }
    if (!repository.files || repository.files.length === 0) {
      return (
        <>
          <ValidationEmoji>👀</ValidationEmoji>
          <ValidationMessage>
            <h5>This repository does not have any files</h5>
            <p>
              You can still commit changes and run the pipeline but it may lead
              to a failure.
            </p>
          </ValidationMessage>
        </>
      );
    }
    if (validator.isValid) {
      return (
        <>
          <ValidationEmoji>🎉</ValidationEmoji>
          <ValidationMessage>
            <h5>Congrats! You&apos;ve configured your first pipeline</h5>
            <p>
              Now you have to commit the .yml file to get it working. It takes a
              couple of minutes.
            </p>
          </ValidationMessage>
        </>
      );
    } else {
      return (
        <>
          <ValidationEmoji>🙁</ValidationEmoji>
          <ValidationMessage>
            <h5>You can&apos;t commit the changes</h5>
            <p>
              We&apos;ve highlighted your errors so you could fix them before
              committing.
            </p>
          </ValidationMessage>
        </>
      );
    }
  }, [sendUIAnalyticsEvent, account, repository, validator]);

  const deploymentsValidationMessage = useMemo(() => {
    if (!repository.userIsAdmin && !capabilities.pipelinesEnabled) {
      return (
        <>
          <ValidationEmoji>🔒</ValidationEmoji>
          <ValidationMessage>
            <h5>You need to be an admin to configure Deployments</h5>
            <p>
              Contact the repo admin to give you admin rights to configure
              Deployments
            </p>
          </ValidationMessage>
        </>
      );
    }
    if (
      repository.hasFetchedCanWriteMainBranch &&
      !repository.canWriteMainBranch
    ) {
      return (
        <>
          <ValidationEmoji>🔒</ValidationEmoji>
          <ValidationMessage>
            <h5>
              Branch permissions on main branch are preventing from adding
              configuration
            </h5>
            <p>
              You can review your{' '}
              <a
                href={getBranchPermissionsPageURL(repository.path)}
                target="_top"
                onClick={() => sendUIAnalyticsEvent('branchPermissionsLink')}
              >
                branch permissions{' '}
              </a>
              or enable Pipelines in{' '}
              <a
                href={getSettingsPageURL(repository.path, account.addonKey)}
                target="_top"
                onClick={() => sendUIAnalyticsEvent('pipelinesSettingsLink')}
              >
                settings
              </a>
            </p>
          </ValidationMessage>
        </>
      );
    }
    if (validator.isValid) {
      return ymlHasDeployments ? (
        <>
          <ValidationEmoji>🎉</ValidationEmoji>
          <ValidationMessage>
            <h5>Your configuration looks good!</h5>
            <p>
              Simply commit the {YML_FILE_NAME} file to kick off your CI/CD
              pipeline.
            </p>
          </ValidationMessage>
        </>
      ) : (
        <>
          <ValidationEmoji>🙁</ValidationEmoji>
          <ValidationMessage>
            <h5>Check your deployment configuration</h5>
            <p>
              We didn&apos;t find the <em>deployment</em> keyword in your{' '}
              {YML_FILE_NAME} file.
            </p>
          </ValidationMessage>
        </>
      );
    } else {
      return (
        <>
          <ValidationEmoji>🙁</ValidationEmoji>
          <ValidationMessage>
            <h5>Your configuration is invalid</h5>
            <p>
              We&apos;ve highlighted your error so you could fix them before
              committing.
            </p>
          </ValidationMessage>
        </>
      );
    }
  }, [
    ymlHasDeployments,
    sendUIAnalyticsEvent,
    account,
    repository,
    capabilities,
    validator,
  ]);

  return (
    <CommitSectionWrapper>
      {validator.currentEditor === EditorEnum.DeploymentsOnboarding &&
        deploymentsValidationMessage}
      {validator.currentEditor === EditorEnum.PipelinesOnboarding &&
        pipelinesValidationMessage}
      <CommitButton>
        {validator.currentEditor === EditorEnum.DeploymentsOnboarding && (
          <LearnMore>
            <Button
              href={DOCS_DEPLOYMENTS}
              onClick={() =>
                sendUIAnalyticsEvent('deploymentsDocumentationLink')
              }
              spacing="none"
              appearance="link"
              target="_blank"
            >
              Learn more
            </Button>
          </LearnMore>
        )}
        <LoadingButton
          appearance="primary"
          onClick={onCommitOrEnable}
          isDisabled={isButtonDisabled}
          isLoading={isEnabling || buildConfiguration.isCommitingError}
        >
          {validator.code === buildConfiguration.yml ? 'Enable' : 'Commit file'}
        </LoadingButton>
      </CommitButton>
    </CommitSectionWrapper>
  );
};

export default React.memo(CommitSection);
